/**
 * Requirements-board derivations, all frontend.
 *
 * The board shows the state of work already recorded — it does not change it. Pipeline
 * status advances as a side effect of doing the work (extracting items, generating RFQs,
 * receiving quotations, sending a quotation), which is why the prototype disables status
 * drag entirely (`wireKanban` is deliberately a no-op) and why there is no endpoint to set
 * an enquiry's status directly. The one manual decision is Won/Rejected on a sent quotation.
 */

import { isKnownBoardStatus } from './api/mappers/status'
import { effectiveStatus } from './format'
import { ENQUIRY_STATUSES, type EnquiryStatus, type EnquirySummary, type Role } from './types'

/**
 * Which flow screen a card opens at — where the work actually continues (Task 7: nine columns).
 *
 * The rule is "the next thing this enquiry needs", not "the last thing done to it". Hence
 * `Comparison Done` → `quote` (2026-08-09): the comparison IS done, so reopening the supplier
 * screen shows finished work and makes the purchaser navigate on by hand. The remaining job is
 * pricing the customer quotation. `/quote/:id` takes the ENQUIRY id (same id every column passes)
 * and, if the allocation somehow produced nothing to quote, says so and links back to
 * `/compare/:id` — so the card can never dead-end.
 */
const STATUS_ROUTE: Record<EnquiryStatus, string> = {
  Draft: 'rfq',
  Open: 'rfq',
  'RFQ Sent': 'suppliers',
  'Quotes Received': 'suppliers',
  'Comparison Done': 'quote',
  'Quotation Sent': 'quote',
  Won: 'quote',
  Rejected: 'quote',
  Archived: 'quote',
}

export const routeForStatus = (status: EnquiryStatus): string => STATUS_ROUTE[status]

/** A card plus the effective status it was placed by — computed ONCE, then carried. */
export interface BoardCard {
  requirement: EnquirySummary
  effective: EnquiryStatus
}

export interface BoardColumn {
  status: EnquiryStatus
  cards: BoardCard[]
}

export interface Board {
  columns: BoardColumn[]
  /** Requirements whose status isn't one of the eight — surfaced, never silently dropped. */
  unplaced: BoardCard[]
}

/**
 * Group requirements into columns by their EFFECTIVE status — a Quotation-Sent enquiry older
 * than 30 days shows in Archived without its stored status being touched (DATA-MODEL).
 *
 * The effective status is computed here and then travels with the card. Task 7: `age_days` is
 * server-computed and fixed per fetch, so there is no `now` to inject any more (unlike the old
 * `status_changed_at`-driven version, which could recompute mid-session as the wall clock moved).
 */
export function groupIntoColumns(requirements: EnquirySummary[]): Board {
  const byStatus = new Map<EnquiryStatus, BoardCard[]>(ENQUIRY_STATUSES.map((s) => [s, []]))
  const unplaced: BoardCard[] = []

  for (const requirement of requirements) {
    const effective = effectiveStatus(requirement.status, requirement.age_days)
    const card: BoardCard = { requirement, effective }
    byStatus.get(effective)?.push(card)
    /*
      A status the client doesn't know (a backend that added one) must still be visible — AND
      must say so.

      This used to be an `else` on the line above, which could never run: `requirement.status` is
      already `EnquiryStatus`, because `toBoardStatus` folded anything unrecognised into `Open`
      before it ever reached here, and `effectiveStatus` only ever returns another `EnquiryStatus`.
      So `byStatus.get()` always hit, `unplaced` was provably always empty, and the board's warning
      banner was dead code. An enquiry with a tenth status piled silently into `Open`, looking
      exactly like one nobody had started.

      Asking the RAW value instead makes the check real. Note it is now ADDITIVE, not an
      either/or: the card is placed in `Open` (visible beats correct-but-hidden) and also listed
      in the banner, so the flag is a warning rather than a disappearance.
    */
    if (!isKnownBoardStatus(requirement.status_raw)) unplaced.push(card)
  }

  return {
    columns: ENQUIRY_STATUSES.map((status) => ({ status, cards: byStatus.get(status) ?? [] })),
    unplaced,
  }
}

/**
 * Per-role board scoping, restored 2026-08-06.
 *
 * This was `false` from 2026-08-05 to 2026-08-06 because the board's `user` was a seed-data stub —
 * `ROLE_USERS[role]`, the literal strings 'Priya'/'Akhil'/'Manoj' from `docs/DATA-MODEL.md`. A real
 * enquiry carries a `User` docname (an email), so `get_enquiries?purchaser=Akhil` matched nothing
 * and every enquiry vanished from the board meant to show it. The same stub later caused a hard
 * failure — `User 'Akhil' not found` on enquiry create.
 *
 * `SessionProvider` now sources both `user` and `role` from `auth.get_current_user`, so the filter
 * is a real email and a server-assigned role. `scopeForRoleByRole` also fails OPEN on an unresolved
 * user, which covers the window before that query settles.
 */
export const SCOPE_BOARD_BY_ROLE = true

/**
 * The server-side filter for the acting role: a manager sees the whole board, a purchaser
 * their assignments, a salesperson the customers they brought in.
 *
 * Returned as query params rather than filtered on the client — the board spans every
 * requirement, and pulling them all back to filter locally is what §3 warns against.
 *
 * An unresolved `user` sends NO filter rather than one that matches nothing: a blank board reads
 * as "you have no work", which is the silent failure this whole stopgap exists because of.
 */
export function scopeForRoleByRole(role: Role, user: string): { purchaser?: string; sales_person?: string } {
  if (role === 'purchase_manager') return {}
  if (user.trim() === '') return {}
  if (role === 'sales') return { sales_person: user }
  return { purchaser: user }
}

/** What the board actually calls. See `SCOPE_BOARD_BY_ROLE` above for why this is a passthrough. */
export function scopeForRole(role: Role, user: string): { purchaser?: string; sales_person?: string } {
  return SCOPE_BOARD_BY_ROLE ? scopeForRoleByRole(role, user) : {}
}

/** Straplines describe what the board IS SHOWING. Scoping is on again, so they can say so. */
export const ROLE_STRAPLINE: Record<Role, string> = {
  sales: 'Requirements for customers you brought in.',
  purchaser: 'Requirements assigned to you. Click a card to open its flow.',
  purchase_manager: 'Manager view — all requirements & purchasers.',
}

/**
 * Whether this requirement can be decided Won/Rejected: only from a sent quotation, and only
 * when there is a quotation to act on. The server enforces both (§9).
 */
export const canDecideRequirement = (requirement: EnquirySummary, effective: EnquiryStatus): boolean =>
  effective === 'Quotation Sent' && !!requirement.quotation_id
